/**
 * Custom Preview Hook for Media Stream Management
 *
 * This hook provides a comprehensive solution for managing video/audio preview streams
 * using the native WebRTC getUserMedia API. It handles browser compatibility, device
 * access permissions, audio level monitoring, and provides a Vonage-compatible API
 * interface for seamless integration.
 *
 * @module useCustomPreview
 */

import { useState, useRef, useCallback } from 'react';
import { DEVICE_ACCESS_STATUS } from '../components/VideoStream/constants';
import useDevices from './useDevices';

/**
 * Audio Level Calculation Constants
 *
 * These constants control the exponential moving average (EMA) smoothing
 * and logarithmic scaling for audio level visualization.
 */
// Weight for previous value in exponential moving average (80%)
const EMA_PREVIOUS_WEIGHT = 0.8;
// Weight for new value in exponential moving average (20%)
const EMA_NEW_WEIGHT = 0.2;
// Scaling factor for logarithmic conversion (maps -30 to 0 dBm range to [0,1])
const LOG_SCALING_FACTOR = 1.5;
// Multiplier to convert normalized value (0-1) to percentage (0-100)
const PERCENTAGE_SCALE = 100;

/**
 * Browser Compatibility Check Function
 *
 * Performs a comprehensive check of browser capabilities required for media streaming.
 * This function validates that the browser supports all necessary APIs including:
 * - getUserMedia for media device access
 * - Web Audio API for audio processing
 * - Video element support for rendering
 * - RequestAnimationFrame for smooth animations
 * - Secure context (HTTPS) for production environments
 *
 * @returns {Object} Compatibility object containing boolean flags for each capability
 * @property {boolean} getUserMedia - Whether getUserMedia API is available
 * @property {boolean} webAudio - Whether Web Audio API is supported
 * @property {boolean} videoElement - Whether video elements are supported
 * @property {boolean} requestAnimationFrame - Whether animation frame API exists
 * @property {boolean} isSecureContext - Whether running in secure context (HTTPS)
 * @property {string} userAgent - Browser user agent string for debugging
 */
const checkBrowserCompatibility = () => {
  // Create compatibility object with all required checks
  // Each property checks for existence of specific browser APIs
  return {
    // Check if getUserMedia is available in navigator.mediaDevices
    getUserMedia: !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia),
    // Check for Web Audio API support (standard and webkit prefix)
    webAudio: !!(window.AudioContext || window.webkitAudioContext),
    // Verify video element can be created and supports playback
    videoElement: !!document.createElement('video').canPlayType,
    // Check for requestAnimationFrame API for smooth frame rendering
    requestAnimationFrame: !!window.requestAnimationFrame,
    // Verify secure context (required for getUserMedia in production)
    isSecureContext: window.isSecureContext || window.location.protocol === 'https:',
    // Store user agent for debugging and compatibility logging
    userAgent: navigator.userAgent
  };

};

/**
 * Get media stream constraints array
 *
 * @returns {Array} Array of constraint objects for progressive fallback
 */
const getMediaConstraints = () => {
  return [
    // High quality constraints (first attempt)
    {
      video: {
        width: { ideal: 1280, max: 1920 },
        height: { ideal: 720, max: 1080 },
        frameRate: { ideal: 30, max: 60 }
      },
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
        sampleRate: 44100
      }
    },
    // Medium quality constraints (second attempt)
    {
      video: {
        width: { ideal: 640, max: 1280 },
        height: { ideal: 480, max: 720 },
        frameRate: { ideal: 24, max: 30 }
      },
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true
      }
    },
    // Basic constraints (third attempt - fallback)
    {
      video: {
        width: { ideal: 320 },
        height: { ideal: 240 },
        frameRate: { ideal: 15 }
      },
      audio: true
    },
    // Minimal constraints (last resort)
    {
      video: true,
      audio: true
    }
  ];
};

/**
 * Attempt to get media stream with progressive constraints
 *
 * @returns {Promise<MediaStream>} Media stream promise
 */
const getMediaStream = async () => {
  const constraints = getMediaConstraints();

  for (const constraint of constraints) {
    try {
      return await navigator.mediaDevices.getUserMedia(constraint);
    } catch {}
  }

  throw new Error('Failed to get media stream with any constraint set');
};

/**
 * Configure video element attributes and styles
 *
 * @param {HTMLVideoElement} videoElement - Video element to configure
 * @param {Function} setAccessAllowed - Function to set access allowed state
 */
const configureVideoElement = (videoElement, setAccessAllowed) => {
  // Set video attributes
  videoElement.autoplay = true;
  videoElement.muted = true;
  videoElement.playsInline = true;
  videoElement.controls = false;
  videoElement.loop = false;

  // Set styles
  videoElement.style.width = '100%';
  videoElement.style.height = '100%';
  videoElement.style.objectFit = 'cover';
  videoElement.style.backgroundColor = '#000';
  videoElement.style.borderRadius = '8px';

  // Add event handlers
  videoElement.onerror = () => {
    setAccessAllowed(DEVICE_ACCESS_STATUS.REJECTED);
  };

};

/**
 * Handle error state based on error type
 *
 * @param {Error} error - Error object
 * @param {Function} setAccessAllowed - Function to set access allowed state
 */
const handleErrorState = (error, setAccessAllowed) => {
  const errorTypes = [
    'NotAllowedError',
    'NotFoundError',
    'NotReadableError',
    'OverconstrainedError',
    'SecurityError'
  ];

  if (errorTypes.includes(error.name)) {
    setAccessAllowed(DEVICE_ACCESS_STATUS.REJECTED);
    return;
  }

  setAccessAllowed(DEVICE_ACCESS_STATUS.REJECTED);
};

/**
 * Clean up media tracks from a stream
 *
 * @param {MediaStream} stream - Media stream to clean up
 */
const cleanupMediaTracks = stream => {
  if (!stream) {
    return;
  }

  stream.getTracks().forEach(track => {
    track.stop();
  });
};

/**
 * Clean up media element (video or audio)
 *
 * @param {HTMLElement} element - Media element to clean up
 */
const cleanupMediaElement = element => {
  if (!element || !element.srcObject) {
    return;
  }

  if (element.srcObject.getTracks) {
    element.srcObject.getTracks().forEach(track => {
      track.stop();
    });
  }

  element.srcObject = null;
};

/**
 * Clean up remaining media elements in document
 */
const cleanupRemainingMediaElements = () => {
  const videos = document.querySelectorAll('video');
  videos.forEach(video => cleanupMediaElement(video));

  const audios = document.querySelectorAll('audio');
  audios.forEach(audio => cleanupMediaElement(audio));
};

/**
 * Helper: Enable audio tracks
 *
 * @param {Object} previewStreamRef - Preview stream ref
 */
const enableAudioTracks = previewStreamRef => {
  if (!previewStreamRef.current) {
    return;
  }
  const audioTracks = previewStreamRef.current.getAudioTracks();
  audioTracks.forEach(track => {
    track.enabled = true;
  });
};

/**
 * Helper: Disable audio tracks
 *
 * @param {Object} previewStreamRef - Preview stream ref
 */
const disableAudioTracks = previewStreamRef => {
  if (!previewStreamRef.current) {
    return;
  }
  const audioTracks = previewStreamRef.current.getAudioTracks();
  audioTracks.forEach(track => {
    track.enabled = false;
  });
};

/**
 * Helper: Enable video tracks
 *
 * @param {Object} previewStreamRef - Preview stream ref
 */
const enableVideoTracks = previewStreamRef => {
  if (!previewStreamRef.current) {
    return;
  }
  const videoTracks = previewStreamRef.current.getVideoTracks();
  videoTracks.forEach(track => {
    track.enabled = true;
  });
};

/**
 * Helper: Disable video tracks
 *
 * @param {Object} previewStreamRef - Preview stream ref
 */
const disableVideoTracks = previewStreamRef => {
  if (!previewStreamRef.current) {
    return;
  }
  const videoTracks = previewStreamRef.current.getVideoTracks();
  videoTracks.forEach(track => {
    track.enabled = false;
  });
};

/**
 * Helper: Check if audio is enabled
 *
 * @param {Object} previewStreamRef - Preview stream ref
 * @returns {boolean} True if audio is enabled
 */
const checkAudioEnabled = previewStreamRef => {
  if (!previewStreamRef.current) {
    return false;
  }
  const audioTracks = previewStreamRef.current.getAudioTracks();
  return audioTracks.length > 0 && audioTracks[0].enabled;
};

/**
 * Helper: Check if video is enabled
 *
 * @param {Object} previewStreamRef - Preview stream ref
 * @returns {boolean} True if video is enabled
 */
const checkVideoEnabled = previewStreamRef => {
  if (!previewStreamRef.current) {
    return false;
  }
  const videoTracks = previewStreamRef.current.getVideoTracks();
  return videoTracks.length > 0 && videoTracks[0].enabled;
};

/**
 * Helper: Get video device information
 *
 * @param {Object} previewStreamRef - Preview stream ref
 * @returns {Object|null} Device info or null
 */
const getVideoDeviceInfo = previewStreamRef => {
  if (!previewStreamRef.current) {
    return null;
  }
  const videoTracks = previewStreamRef.current.getVideoTracks();
  if (videoTracks.length === 0) {
    return null;
  }
  return {
    deviceId: videoTracks[0].getSettings().deviceId,
    label: videoTracks[0].label
  };
};

/**
 * Helper: Get audio device information
 *
 * @param {Object} previewStreamRef - Preview stream ref
 * @returns {Object|null} Device info or null
 */
const getAudioDeviceInfo = previewStreamRef => {
  if (!previewStreamRef.current) {
    return null;
  }
  const audioTracks = previewStreamRef.current.getAudioTracks();
  if (audioTracks.length === 0) {
    return null;
  }
  return {
    deviceId: audioTracks[0].getSettings().deviceId,
    label: audioTracks[0].label
  };
};

/**
 * Helper: Validate audio monitoring prerequisites
 *
 * @param {Object} previewStreamRef - Preview stream ref
 * @returns {boolean} True if prerequisites are met
 */
const validateAudioPrerequisites = previewStreamRef => {
  if (!previewStreamRef.current) {
    return false;
  }
  const audioTracks = previewStreamRef.current.getAudioTracks();
  if (audioTracks.length === 0) {
    return false;
  }
  const AudioContextClass = window.AudioContext || window.webkitAudioContext;
  return !!AudioContextClass;
};

/**
 * Helper: Setup audio context and analyser
 *
 * @param {Object} previewStreamRef - Preview stream ref
 * @returns {Object} Object with audio context, analyser, and data array
 */
const setupAudioMonitoring = previewStreamRef => {
  const AudioContextClass = window.AudioContext || window.webkitAudioContext;
  const context = new AudioContextClass();
  if (context.state === 'suspended') {
    context.resume().catch();
  }
  const source = context.createMediaStreamSource(previewStreamRef.current);
  const analyserNode = context.createAnalyser();
  analyserNode.fftSize = 256;
  analyserNode.smoothingTimeConstant = 0.8;
  source.connect(analyserNode);
  const dataArray = new Uint8Array(analyserNode.frequencyBinCount);
  return { context, analyserNode, dataArray };
};

/**
 * Helper: Calculate average audio level from frequency data
 *
 * @param {Uint8Array} array - Frequency data array
 * @returns {number} Normalized audio level (0-1)
 */
const calculateAverageLevel = array => {
  let sum = 0;
  const MAX_VALUE = 255;
  for (const value of array) {
    sum += value;
  }
  return (sum / array.length) / MAX_VALUE;
};

/**
 * Helper: Validate browser compatibility
 *
 * @throws {Error} If browser doesn't support required APIs
 */
const validateBrowserCompatibility = () => {
  const compatibility = checkBrowserCompatibility();
  if (!compatibility.getUserMedia) {
    throw new Error('getUserMedia is not supported in this browser');
  }
  if (!compatibility.isSecureContext) {
    throw new Error('getUserMedia requires HTTPS in production');
  }
  if (!compatibility.videoElement) {
    throw new Error('Video element is not supported in this browser');
  }
};

/**
 * Helper: Create and setup video element
 *
 * @param {Object} previewVideoRef - Preview video ref
 * @param {Function} setAccessAllowed - Function to set access allowed state
 * @returns {HTMLVideoElement} Configured video element
 */
const createVideoElement = (previewVideoRef, setAccessAllowed) => {
  if (previewVideoRef.current) {
    return previewVideoRef.current;
  }
  const videoElement = document.createElement('video');
  configureVideoElement(videoElement, setAccessAllowed);
  previewVideoRef.current = videoElement;
  return videoElement;
};

/**
 * Helper: Clean up video element
 *
 * @param {Object} previewVideoRef - Preview video ref
 */
const cleanupVideoElement = previewVideoRef => {
  if (!previewVideoRef.current) {
    return;
  }
  try {
    previewVideoRef.current.pause();
    previewVideoRef.current.currentTime = 0;
    previewVideoRef.current.srcObject = null;
    previewVideoRef.current.onerror = null;
    previewVideoRef.current.onloadstart = null;
    previewVideoRef.current.oncanplay = null;
    if (previewVideoRef.current.parentNode) {
      previewVideoRef.current.parentNode.removeChild(previewVideoRef.current);
    }
  } catch {}
  previewVideoRef.current = null;
};

/**
 * Helper: Clean up preview stream tracks
 *
 * @param {Object} previewStreamRef - Preview stream ref
 */
const cleanupPreviewStream = previewStreamRef => {
  if (!previewStreamRef.current) {
    return;
  }
  try {
    cleanupMediaTracks(previewStreamRef.current);
  } catch {}
  previewStreamRef.current = null;
};

/**
 * Helper: Stop monitoring on error
 *
 * @param {Object} animationFrameRef - Animation frame ref
 */
const stopMonitoringOnError = animationFrameRef => {
  if (animationFrameRef.current) {
    cancelAnimationFrame(animationFrameRef.current);
    animationFrameRef.current = null;
  }
};

/**
 * Helper: Handle suspended audio context
 *
 * @param {Object} audioContextRef - Audio context ref
 * @param {Object} animationFrameRef - Animation frame ref
 * @param {Function} monitorLoop - Monitoring loop function
 */
const handleSuspendedContext = (audioContextRef, animationFrameRef, monitorLoop) => {
  audioContextRef.current.resume().then(() => {
    animationFrameRef.current = requestAnimationFrame(monitorLoop);
  }).catch();
};

/**
 * Helper: Handle non-running audio context
 *
 * @param {Object} animationFrameRef - Animation frame ref
 */
const handleNonRunningContext = animationFrameRef => {
  if (animationFrameRef.current) {
    cancelAnimationFrame(animationFrameRef.current);
    animationFrameRef.current = null;
  }
};

/**
 * Helper: Handle audio context state
 *
 * @param {Object} audioContextRef - Audio context ref
 * @param {Object} animationFrameRef - Animation frame ref
 * @param {Function} monitorLoop - Monitoring loop function
 */
const handleAudioContextState = (audioContextRef, animationFrameRef, monitorLoop) => {
  if (!audioContextRef.current) {
    return;
  }
  if (audioContextRef.current.state === 'suspended') {
    handleSuspendedContext(audioContextRef, animationFrameRef, monitorLoop);
    return;
  }
  if (audioContextRef.current.state !== 'running') {
    handleNonRunningContext(animationFrameRef);
  }
};

/**
 * Helper: Process running audio frame
 *
 * @param {AnalyserNode} analyserNode - Analyser node
 * @param {Uint8Array} dataArray - Data array
 * @param {Function} calculateAudioLevelFn - Function to calculate audio level
 * @param {Object} animationFrameRef - Animation frame ref
 * @param {Function} monitorLoop - Monitoring loop function
 */
const processRunningAudioFrame = (analyserNode, dataArray, calculateAudioLevelFn, animationFrameRef, monitorLoop) => {
  analyserNode.getByteFrequencyData(dataArray);
  const normalizedLevel = calculateAverageLevel(dataArray);
  calculateAudioLevelFn(normalizedLevel);
  animationFrameRef.current = requestAnimationFrame(monitorLoop);
};

/**
 * Helper: Create audio monitoring loop
 *
 * @param {AnalyserNode} analyserNode - Analyser node
 * @param {Uint8Array} dataArray - Data array
 * @param {Function} calculateAudioLevelFn - Function to calculate audio level
 * @param {Object} previewStreamRef - Preview stream ref
 * @param {Object} audioContextRef - Audio context ref
 * @param {Object} animationFrameRef - Animation frame ref
 * @returns {Function} Monitoring loop function
 */
const createAudioMonitoringLoopHelper = (analyserNode, dataArray, calculateAudioLevelFn, previewStreamRef, audioContextRef, animationFrameRef) => {
  const monitorAudioLevel = () => {
    try {
      if (!analyserNode || !previewStreamRef.current || !audioContextRef.current) {
        return;
      }
      if (audioContextRef.current.state === 'running') {
        processRunningAudioFrame(analyserNode, dataArray, calculateAudioLevelFn, animationFrameRef, monitorAudioLevel);
        return;
      }
      handleAudioContextState(audioContextRef, animationFrameRef, monitorAudioLevel);
    } catch {
      stopMonitoringOnError(animationFrameRef);
    }
  };
  return monitorAudioLevel;
};

/**
 * Helper: Attach stream to video element and append to DOM
 *
 * @param {HTMLVideoElement} videoElement - Video element
 * @param {MediaStream} stream - Media stream
 * @param {HTMLElement} targetEl - Target DOM element
 */
const setupVideoInDOM = (videoElement, stream, targetEl) => {
  videoElement.srcObject = stream;
  if (!targetEl) {
    return;
  }
  targetEl.innerHTML = '';
  targetEl.appendChild(videoElement);
};

/**
 * Helper: Reset component state
 *
 * @param {Function} setPreviewMediaCreated - Function to set preview media created state
 * @param {Function} setAccessAllowed - Function to set access allowed state
 * @param {Function} setLogLevel - Function to set log level state
 */
const resetState = (setPreviewMediaCreated, setAccessAllowed, setLogLevel) => {
  setPreviewMediaCreated(false);
  setAccessAllowed(DEVICE_ACCESS_STATUS.PENDING);
  setLogLevel(0);
};

/**
 * Custom Preview Hook
 *
 * Main hook function that manages video/audio preview functionality.
 * Provides methods to create, destroy, and control media preview streams
 * with comprehensive error handling and browser compatibility checks.
 *
 * @returns {Object} Hook return object containing preview controls and state
 */
export default function useCustomPreview() {
  // Refs for managing media stream and DOM elements
  // These refs persist across re-renders without causing component updates
  // Stores the MediaStream object from getUserMedia
  const previewStream = useRef();
  // Reference to the video DOM element
  const previewVideo = useRef();
  // Web Audio API context for audio analysis
  const audioContext = useRef();
  // Analyser node for audio level calculations
  const analyser = useRef();
  // Animation frame ID for audio monitoring loop
  const animationFrame = useRef();

  // State management for preview functionality
  // logLevel: Current audio level (0-100) for visual feedback
  const [logLevel, setLogLevel] = useState(0);
  // previewMediaCreated: Boolean flag indicating if preview stream is active
  const [previewMediaCreated, setPreviewMediaCreated] = useState(false);
  // accessAllowed: Current status of device access permission
  // Can be PENDING, ACCEPTED, or REJECTED
  const [accessAllowed, setAccessAllowed] = useState(
    DEVICE_ACCESS_STATUS.PENDING
  );
  // Get device information and device enumeration function from useDevices hook
  const { deviceInfo, getDevices } = useDevices();

  /**
   * Calculate Audio Level with Exponential Moving Average
   */
  const calculateAudioLevel = useCallback(audioLevel => {
    let movingAvg = null;
    if (movingAvg === null || movingAvg <= audioLevel) {
      movingAvg = audioLevel;
    } else {
      movingAvg = EMA_PREVIOUS_WEIGHT * movingAvg + EMA_NEW_WEIGHT * audioLevel;
    }
    const currentLogLevel = Math.log(movingAvg) / Math.LN10 / LOG_SCALING_FACTOR + 1;
    setLogLevel(Math.min(Math.max(currentLogLevel, 0), 1) * PERCENTAGE_SCALE);
  }, [setLogLevel]);


  /**
   * Create audio monitoring loop
   */
  const createAudioMonitoringLoop = useCallback((analyserNode, dataArray, calculateAudioLevelFn) => {
    return createAudioMonitoringLoopHelper(
      analyserNode,
      dataArray,
      calculateAudioLevelFn,
      previewStream,
      audioContext,
      animationFrame
    );
  }, []);

  /**
   * Start Audio Level Monitoring
   */
  const startAudioLevelMonitoring = useCallback(() => {
    if (!validateAudioPrerequisites(previewStream)) {
      return;
    }
    try {
      const { context, analyserNode, dataArray } = setupAudioMonitoring(previewStream);
      audioContext.current = context;
      analyser.current = analyserNode;
      const monitorAudioLevel = createAudioMonitoringLoop(analyserNode, dataArray, calculateAudioLevel);
      monitorAudioLevel();
    } catch {}
  }, [calculateAudioLevel, createAudioMonitoringLoop]);

  /**
   * Stop Audio Level Monitoring
   */
  const stopAudioLevelMonitoring = useCallback(() => {
    if (animationFrame.current) {
      cancelAnimationFrame(animationFrame.current);
      animationFrame.current = null;
    }
    if (audioContext.current) {
      audioContext.current.close();
      audioContext.current = null;
    }
    analyser.current = null;
  }, []);



  /**
   * Create Preview Stream
   *
   * Main function to initialize and display video/audio preview.
   *
   * @param {HTMLElement} targetEl - DOM element where video preview will be rendered
   * @param {Object} publisherOptions - Optional configuration object (reserved for future use)
   * @returns {Promise<void>} Resolves when preview is created successfully
   */
  const createPreview = useCallback(
    async targetEl => {
      try {
        validateBrowserCompatibility();
        if (!targetEl) {
          throw new Error('Target element not provided for preview');
        }
        const stream = await getMediaStream();
        previewStream.current = stream;
        const videoElement = createVideoElement(previewVideo, setAccessAllowed);
        setupVideoInDOM(videoElement, stream, targetEl);
        startAudioLevelMonitoring();
        setAccessAllowed(DEVICE_ACCESS_STATUS.ACCEPTED);
        setPreviewMediaCreated(true);
        getDevices();
      } catch (err) {
        if (previewStream.current) {
          cleanupMediaTracks(previewStream.current);
          previewStream.current = null;
        }
        handleErrorState(err, setAccessAllowed);
        setPreviewMediaCreated(false);
      }
    },
    [startAudioLevelMonitoring, getDevices]
  );


  /**
   * Destroy Preview Stream
   *
   * Comprehensive cleanup function that releases all preview resources.
   */
  const destroyPreview = useCallback(() => {
    try {
      stopAudioLevelMonitoring();
      cleanupPreviewStream(previewStream);
      cleanupVideoElement(previewVideo);
      try {
        cleanupRemainingMediaElements();
      } catch {}
      resetState(setPreviewMediaCreated, setAccessAllowed, setLogLevel);
    } catch {
      resetState(setPreviewMediaCreated, setAccessAllowed, setLogLevel);
    }
  }, [stopAudioLevelMonitoring, setPreviewMediaCreated, setAccessAllowed, setLogLevel]);


  /**
   * Enable Audio
   */
  const enableAudio = useCallback(() => {
    enableAudioTracks(previewStream);
  }, []);

  /**
   * Disable Audio
   */
  const disableAudio = useCallback(() => {
    disableAudioTracks(previewStream);
  }, []);

  /**
   * Enable Video
   */
  const enableVideo = useCallback(() => {
    enableVideoTracks(previewStream);
  }, []);

  /**
   * Disable Video
   */
  const disableVideo = useCallback(() => {
    disableVideoTracks(previewStream);
  }, []);

  /**
   * Check if Audio is Enabled
   */
  const isAudioEnabled = useCallback(() => checkAudioEnabled(previewStream), []);

  /**
   * Check if Video is Enabled
   */
  const isVideoEnabled = useCallback(() => checkVideoEnabled(previewStream), []);

  /**
   * Get Video Device Information
   */
  const getVideoDevice = useCallback(() => getVideoDeviceInfo(previewStream), []);

  /**
   * Get Audio Device Information
   */
  const getAudioDevice = useCallback(() => getAudioDeviceInfo(previewStream), []);

  /**
   * Preview Publisher Object
   *
   * Creates a mock preview publisher object that matches the Vonage API interface.
   */
  const previewPublisher = useRef({
    enableAudio,
    disableAudio,
    enableVideo,
    disableVideo,
    isAudioEnabled,
    isVideoEnabled,
    getVideoDevice,
    getAudioDevice
  });

  /**
   * Hook Return Object
   *
   * Returns all preview functionality and state for component consumption.
   */
  return {
    previewPublisher: previewPublisher.current,
    createPreview,
    destroyPreview,
    logLevel,
    previewMediaCreated,
    accessAllowed,
    deviceInfo
  };
}
